import { Box, Text } from '@chakra-ui/layout';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/tabs';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../../../../../../../../../../../../../../hooks/auth';
import { ICalendarEventContextState } from '../../../../../../../../../../../../../../../hooks/calendarEvent';
import { WaveSideEnum } from '../../../../../../../../../../../../../../../models/activities';
import { BookingQueueTable } from '../../../../../ActivitySchedules/components/ActivityScheduleBookingQueueModal/components/BookingQueueTable';
import { ScheduleBookingsTable } from './components/ScheduleBookingsTable';
import { ScheduleSessionBlocksTable } from './components/ScheduleSessionBlocksTable';

interface IScheduleTabsProps {
  event: ICalendarEventContextState;
}

export const ScheduleTabs = ({ event }: IScheduleTabsProps): JSX.Element => {
  const { assignment: authenticatedAssignment, experienceLevelsTitleMap } =
    useAuth();

  const [tabIndex, setTabIndex] = useState(0);

  const canReadBookingQueues =
    authenticatedAssignment?.featureGroup.features.some((feature) =>
      ['BOOKING_QUEUE_FULL_ACCESS', 'BOOKING_QUEUE_READ_ACCESS'].includes(
        feature.key,
      ),
    );
  const canReadSessionBlocks =
    authenticatedAssignment?.featureGroup.features.some((feature) =>
      [
        'BOOKING_FULL_ACCESS',
        'BOOKING_READ_ACCESS',
        'BOOKING_WRITE_ACCESS',
        'BOOKING_QUEUE_FULL_ACCESS',
        'BOOKING_QUEUE_READ_ACCESS',
      ].includes(feature.key),
    );
  const bookedDate = event.start.toLocaleDateString('en-CA');
  const scheduleIds = useMemo(
    () => event.schedules.map((schedule) => schedule.activityScheduleId),
    [event.schedules],
  );

  const handleTabChanges = useCallback((index: number) => {
    setTabIndex(index);
  }, []);

  return (
    <Tabs
      index={tabIndex}
      isLazy
      onChange={handleTabChanges}
      variant="enclosed-colored"
      mt="6"
    >
      <TabList>
        <Tab>Reservas</Tab>

        {canReadBookingQueues && <Tab>Fila de espera</Tab>}

        {canReadSessionBlocks && <Tab>Paralisações</Tab>}
      </TabList>

      <TabPanels mt="4">
        <TabPanel>
          {event.schedules.length > 1 ? (
            event.schedules.map((schedule) => (
              <>
                {schedule.waveSide && (
                  <Box>
                    <Text as="span" fontWeight="bold" pr="2">
                      Lado da onda:
                    </Text>
                    <Text as="span">{WaveSideEnum[schedule.waveSide]}</Text>
                  </Box>
                )}

                {schedule.experience >= 0 && (
                  <Box>
                    <Text as="span" fontWeight="bold" pr="2">
                      Experiência:
                    </Text>
                    <Text as="span">
                      {experienceLevelsTitleMap.get(schedule.experience)}
                    </Text>
                  </Box>
                )}

                {schedule.modalityTitle && (
                  <Box>
                    <Text as="span" fontWeight="bold" pr="2">
                      Modalidade:
                    </Text>
                    <Text as="span">{schedule.modalityTitle}</Text>
                  </Box>
                )}
                <ScheduleBookingsTable
                  activityScheduleId={schedule.activityScheduleId}
                  bookedDate={bookedDate}
                  eventId={event.id}
                />
              </>
            ))
          ) : (
            <ScheduleBookingsTable
              activityScheduleId={event.schedules[0].activityScheduleId}
              bookedDate={bookedDate}
              eventId={event.id}
            />
          )}
        </TabPanel>

        {canReadBookingQueues && (
          <TabPanel>
            {event.schedules.length > 1 ? (
              event.schedules.map((schedule) => (
                <>
                  {schedule.waveSide && (
                    <Box>
                      <Text as="span" fontWeight="bold" pr="2">
                        Lado da onda:
                      </Text>
                      <Text as="span">{WaveSideEnum[schedule.waveSide]}</Text>
                    </Box>
                  )}

                  {schedule.experience >= 0 && (
                    <Box>
                      <Text as="span" fontWeight="bold" pr="2">
                        Experiência:
                      </Text>
                      <Text as="span">
                        {experienceLevelsTitleMap.get(schedule.experience)}
                      </Text>
                    </Box>
                  )}

                  {schedule.modalityTitle && (
                    <Box>
                      <Text as="span" fontWeight="bold" pr="2">
                        Modalidade:
                      </Text>
                      <Text as="span">{schedule.modalityTitle}</Text>
                    </Box>
                  )}

                  <BookingQueueTable
                    flex={1}
                    activityScheduleId={schedule.activityScheduleId}
                    bookedDate={event.start}
                    mb="10"
                  />
                </>
              ))
            ) : (
              <BookingQueueTable
                flex={1}
                activityScheduleId={event.schedules[0].activityScheduleId}
                bookedDate={event.start}
                mb="10"
              />
            )}
          </TabPanel>
        )}

        {canReadSessionBlocks && (
          <TabPanel>
            <ScheduleSessionBlocksTable
              activityId={event.activityId}
              bookedDate={bookedDate}
              scheduleIds={scheduleIds}
            />
          </TabPanel>
        )}
      </TabPanels>
    </Tabs>
  );
};
